import React from "react";
import { Layout, Menu, Grid, Drawer, Button, Badge, theme } from "antd";
import {
  UnorderedListOutlined,
  BarsOutlined,
  LeftOutlined,
  RightOutlined,
  HomeOutlined,
  CalendarOutlined,
  WarningOutlined,
  ApartmentOutlined,
  TeamOutlined,
  FileDoneOutlined,
  EnvironmentOutlined,
  UserOutlined,
  ExperimentOutlined,
} from "@ant-design/icons";
import { useTicketsUnreadCount } from "../hooks";
import {
  type TreeMenuItem,
  CanAccess,
  useMenu,
  useLink,
  useGetIdentity,
} from "@refinedev/core";
import { useThemedLayoutContext } from "@refinedev/antd";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import type { UserInfo } from "../types/auth";
import {
  ADMIN_USER_TYPES,
  AUDITOR_USER_TYPES,
  isEurosemillasScoped,
} from "../types/auth";

const isAdminUser = (identity: UserInfo | undefined | null): boolean =>
  (ADMIN_USER_TYPES as readonly number[]).includes(
    identity?.app_user?.user_type ?? -1,
  );

const isAuditorUser = (identity: UserInfo | undefined | null): boolean =>
  (AUDITOR_USER_TYPES as readonly number[]).includes(
    identity?.app_user?.user_type ?? -1,
  );

const ADMIN_ONLY_RESOURCES = [
  "producers",
  "farms",
  "incidences",
  "farm-production",
  "farm-production-records",
  "admin",
  "samples",
  "audit-companies",
  "auditor-assignments",
  "admin-users",
  "admin-tickets",
  "ticket-categories",
  "business-positions",
  "admin-seasons",
  "onboarding",
];

/**
 * Override selectedKey for cross-resource routes.
 * e.g. /producers/:id/farms/* should highlight /farms
 */
const resolveSelectedKey = (
  pathname: string,
  defaultKey: string,
  isAuditor: boolean,
  isEurosemillas: boolean,
): string => {
  if (/^\/producers\/\d+\/farms/.test(pathname)) return "/farms";
  // The "Inicio" entry now points to the new per-role dashboard
  // (`/dashboard`) — keep it highlighted there *and* on the legacy
  // `/auditor/home` route so anyone with a bookmark still sees
  // their menu state preserved.
  if (
    pathname === "/dashboard" ||
    pathname.startsWith("/dashboard/") ||
    pathname.startsWith("/auditor/home") ||
    pathname === "/auditor"
  )
    return "auditor-home";
  if (pathname.startsWith("/auditor/planning-groups")) return "auditor-plannings";
  // Eurosemillas: rutas admin-shape (/planning-groups, /audits, /farms,
  // /producers) — reutilizan las mismas páginas que Cyber Admin pero
  // scoped por país en backend. Menu highlight solo cuando aplica el
  // menu ESA (no queremos afectar el highlight de Cyber Admin).
  if (isEurosemillas) {
    if (
      pathname === "/planning-groups" ||
      pathname.startsWith("/planning-groups/")
    )
      return "esa-plannings";
    if (pathname === "/audits" || pathname.startsWith("/audits/"))
      return "esa-audits";
    if (pathname === "/farms" || pathname.startsWith("/farms/"))
      return "esa-farms";
    if (pathname === "/producers" || pathname.startsWith("/producers/"))
      return "esa-producers";
    if (pathname === "/dashboard" || pathname.startsWith("/dashboard/"))
      return "esa-home";
    if (pathname === "/admin/tickets" || pathname.startsWith("/admin/tickets/"))
      return "esa-incidences";
  }
  if (pathname.startsWith("/auditor/assign")) return "auditor-assign";
  if (pathname.startsWith("/auditor/auditors")) return "auditor-auditors";
  if (pathname.startsWith("/auditor/incidences")) return "auditor-incidences";
  if (pathname.startsWith("/auditor/samples")) return "auditor-samples-pending";
  // When an auditor opens an audit detail / edit / sub-route from
  // their planning context, keep "Planificaciones" highlighted so
  // they don't lose place in the sidebar. The audit list is not
  // even part of their menu.
  if (isAuditor && (pathname === "/audits" || pathname.startsWith("/audits/"))) {
    return "auditor-plannings";
  }
  // Mantener "Onboarding" resaltado en cualquier sub-ruta
  // (/onboarding/capitulos/*, /documentos, /videos, /faq, y también
  // /onboarding/admin/assets).
  //
  // Refine construye la key del menú como `/${identifier ?? name}`
  // (ver `createResourceKey` en @refinedev/core). El resource está
  // declarado como `name: "onboarding"` sin identifier → key =
  // `/onboarding`, NO `onboarding`. Devolver la string sin slash no
  // matchea y el item nunca se resalta.
  if (pathname === "/onboarding" || pathname.startsWith("/onboarding/")) {
    return "/onboarding";
  }
  return defaultKey;
};

/** Menu entries del perfil Eurosemillas (user_type=2 + portfolio).
 *  Menú corto y read-focused: dashboard + los 4 modelos scopeados
 *  (plannings, audits, farms, producers). No incluye Administración
 *  ni catálogos — esos requieren Cyber Admin. */
interface EsaMenuEntry {
  key: string;
  label: React.ReactNode;
  to: string;
  icon: React.ReactNode;
}

interface AuditorMenuEntry {
  key: string;
  label: React.ReactNode;
  to: string;
  icon: React.ReactNode;
}

export const CustomSider: React.FC = () => {
  const { token } = theme.useToken();
  const { siderCollapsed, setSiderCollapsed, mobileSiderOpen, setMobileSiderOpen } =
    useThemedLayoutContext();
  const { data: identity } = useGetIdentity<UserInfo>();
  const isAdmin = isAdminUser(identity);
  const isAuditor = isAuditorUser(identity);
  const isEurosemillas = isEurosemillasScoped(identity);
  const Link = useLink();
  const { menuItems, selectedKey: defaultSelectedKey, defaultOpenKeys } = useMenu();
  const breakpoint = Grid.useBreakpoint();
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const selectedKey = resolveSelectedKey(
    pathname, defaultSelectedKey, isAuditor, isEurosemillas,
  );
  const isMobile = typeof breakpoint.lg === "undefined" ? false : !breakpoint.lg;

  // Unread tickets badge appears next to the "Incidencias" entry. Polling is
  // light (60s) and doesn't run for non-auditor users.
  const ticketsUnread = useTicketsUnreadCount();

  const isCoordinator = identity?.app_user?.user_type === 4;

  const auditorEntries: AuditorMenuEntry[] = [
    {
      key: "auditor-home",
      label: t("auditor_section.menu.home"),
      to: "/dashboard",
      icon: <HomeOutlined />,
    },
    {
      key: "auditor-plannings",
      label: t("auditor_section.menu.plannings"),
      to: "/auditor/planning-groups",
      icon: <CalendarOutlined />,
    },
    {
      key: "auditor-samples-pending",
      label: t("auditor_section.menu.samples_pending", "Muestras pendientes"),
      to: "/auditor/samples/pending",
      icon: <ExperimentOutlined />,
    },
    ...(isCoordinator
      ? [
          {
            key: "auditor-assign",
            label: t("auditor_section.menu.assign", "Asignación"),
            to: "/auditor/assign",
            icon: <ApartmentOutlined />,
          } as AuditorMenuEntry,
          {
            key: "auditor-auditors",
            label: t("auditor_section.menu.auditors", "Auditores"),
            to: "/auditor/auditors",
            icon: <TeamOutlined />,
          } as AuditorMenuEntry,
        ]
      : []),
    {
      key: "auditor-incidences",
      label: (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          {t("auditor_section.menu.incidences")}
          {ticketsUnread.tickets > 0 && (
            <Badge
              count={ticketsUnread.tickets}
              size="small"
              style={{ backgroundColor: "#FF8C00" }}
            />
          )}
        </span>
      ),
      to: "/auditor/incidences",
      icon: <WarningOutlined />,
    },
  ];

  const esaEntries: EsaMenuEntry[] = [
    {
      key: "esa-home",
      label: t("eurosemillas.menu.home", "Inicio"),
      to: "/dashboard",
      icon: <HomeOutlined />,
    },
    {
      key: "esa-plannings",
      label: t("eurosemillas.menu.plannings", "Planificaciones"),
      to: "/planning-groups",
      icon: <CalendarOutlined />,
    },
    {
      key: "esa-audits",
      label: t("eurosemillas.menu.audits", "Auditorías"),
      to: "/audits",
      icon: <FileDoneOutlined />,
    },
    {
      key: "esa-farms",
      label: t("eurosemillas.menu.farms", "Fincas"),
      to: "/farms",
      icon: <EnvironmentOutlined />,
    },
    {
      key: "esa-producers",
      label: t("eurosemillas.menu.producers", "Productores"),
      to: "/producers",
      icon: <UserOutlined />,
    },
    {
      key: "esa-incidences",
      label: t("eurosemillas.menu.incidences", "Incidencias"),
      to: "/admin/tickets",
      icon: <WarningOutlined />,
    },
  ];

  // Filter admin-only items for non-admin users
  const filteredMenuItems = isAdmin
    ? menuItems
    : menuItems.filter((item) => !ADMIN_ONLY_RESOURCES.includes(item.name)).map((item) => ({
        ...item,
        children: item.children.filter((child) => !ADMIN_ONLY_RESOURCES.includes(child.name)),
      }));

  const renderTreeView = (tree: TreeMenuItem[]) =>
    tree.map((item) => {
      const { key, name, children, meta, list } = item;
      const parentName = meta?.parent;
      const label = item?.label ?? meta?.label ?? name;
      const icon = meta?.icon;
      const route = list;

      if (children.length > 0) {
        return (
          <CanAccess key={key} resource={name} action="list" params={{ resource: item }}>
            <Menu.SubMenu key={key} icon={icon ?? <UnorderedListOutlined />} title={label}>
              {renderTreeView(children)}
            </Menu.SubMenu>
          </CanAccess>
        );
      }

      const isSelected = key === selectedKey;
      const isRoute = !(parentName !== undefined && children.length === 0);
      const linkStyle: React.CSSProperties = isSelected ? { pointerEvents: "none" } : {};

      return (
        <CanAccess key={key} resource={name} action="list" params={{ resource: item }}>
          <Menu.Item key={key} icon={icon ?? (isRoute && <UnorderedListOutlined />)} style={linkStyle}>
            <Link to={route ?? ""} style={linkStyle}>
              {label}
            </Link>
          </Menu.Item>
        </CanAccess>
      );
    });

  /**
   * Order of precedence when rendering el menu lateral:
   *  1. Eurosemillas scoped (ESA Admin con portfolio) → menu ESA
   *     (dedicated corto: dashboard + plannings + audits + farms +
   *     productores). Va PRIMERO porque tiene prioridad sobre la
   *     rama admin — el mismo user_type=2 se pintaría por default
   *     el full menu admin.
   *  2. Auditor puro (auditor + no admin) → menu auditor.
   *  3. Resto (Cyber Admin, admin sin portfolio, comercial…) →
   *     tree view completo del provider Refine.
   */
  const renderSimpleEntries = (
    entries: Array<{
      key: string;
      icon: React.ReactNode;
      to: string;
      label: React.ReactNode;
    }>,
  ) =>
    entries.map((entry) => {
      const isSelected = entry.key === selectedKey;
      const linkStyle: React.CSSProperties = isSelected
        ? { pointerEvents: "none" }
        : {};
      return (
        <Menu.Item key={entry.key} icon={entry.icon} style={linkStyle}>
          <Link to={entry.to} style={linkStyle}>
            {entry.label}
          </Link>
        </Menu.Item>
      );
    });

  const items = isEurosemillas
    ? renderSimpleEntries(esaEntries)
    : isAuditor && !isAdmin
      ? renderSimpleEntries(auditorEntries)
      : renderTreeView(filteredMenuItems);

  const renderMenu = () => (
    <Menu
      selectedKeys={selectedKey ? [selectedKey] : []}
      defaultOpenKeys={defaultOpenKeys}
      mode="inline"
      style={{ paddingTop: "8px", border: "none", overflow: "auto", height: "calc(100% - 72px)" }}
      onClick={() => setMobileSiderOpen(false)}
    >
      {items}
    </Menu>
  );

  const TitleComponent = ({ collapsed }: { collapsed: boolean }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: collapsed ? "12px 0" : "12px",
        height: "64px",
        width: "100%",
      }}
    >
      <img
        src="/logo_cyberagropolis.png"
        alt="Cyberagrópolis"
        style={{
          maxWidth: collapsed ? "40px" : "160px",
          height: "auto",
          maxHeight: "48px",
          objectFit: "contain",
          transition: "max-width 0.2s ease",
        }}
      />
    </div>
  );

  if (isMobile) {
    return (
      <>
        <Drawer
          open={mobileSiderOpen}
          onClose={() => setMobileSiderOpen(false)}
          placement="left"
          closable={false}
          width={200}
          styles={{ body: { padding: 0 } }}
          maskClosable
        >
          <Layout>
            <Layout.Sider style={{ height: "100vh", backgroundColor: token.colorBgContainer, borderRight: `1px solid ${token.colorBgElevated}` }}>
              <div style={{ width: "200px", padding: "0 16px", display: "flex", justifyContent: "flex-start", alignItems: "center", height: "64px", backgroundColor: token.colorBgElevated }}>
                <TitleComponent collapsed={false} />
              </div>
              {renderMenu()}
            </Layout.Sider>
          </Layout>
        </Drawer>
        <Button style={{ position: "fixed", top: 64, left: 0, zIndex: 999, borderRadius: "0 6px 6px 0" }} size="large" onClick={() => setMobileSiderOpen(true)} icon={<BarsOutlined />} />
      </>
    );
  }

  return (
    <>
      <Layout.Sider
        style={{ backgroundColor: token.colorBgContainer, borderRight: `1px solid ${token.colorBgElevated}` }}
        collapsible
        collapsed={siderCollapsed}
        onCollapse={(collapsed, type) => { if (type === "clickTrigger") setSiderCollapsed(collapsed); }}
        collapsedWidth={80}
        breakpoint="lg"
        trigger={
          <Button type="text" style={{ borderRadius: 0, height: "100%", width: "100%", backgroundColor: token.colorBgElevated }}>
            {siderCollapsed ? <RightOutlined style={{ color: token.colorPrimary }} /> : <LeftOutlined style={{ color: token.colorPrimary }} />}
          </Button>
        }
      >
        <div
          style={{
            width: siderCollapsed ? "80px" : "200px",
            padding: siderCollapsed ? "0" : "0 16px",
            display: "flex",
            justifyContent: siderCollapsed ? "center" : "flex-start",
            alignItems: "center",
            height: "64px",
            backgroundColor: token.colorBgElevated,
            fontSize: "14px",
          }}
        >
          <TitleComponent collapsed={siderCollapsed} />
        </div>
        {renderMenu()}
      </Layout.Sider>
    </>
  );
};
